'use client';

import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { useCart } from '@sola/cart-sdk/store.tsx';
import { ErrorCart } from '@sola/schema/dotcom/cart/errors.ts';
import { AvailabilityErrorTypeEnum } from '@sola/schema/dotcom/shared/am/errors.ts';
import { trackError } from '@sola/tracking/components/datalayer.tsx';
type UseChildSponsorshipReturn = {
  isSubmitting: boolean;
  bioErrors?: ErrorCart[];
  handleSponsorshipSubmit: () => Promise<void>;
};
export const useChildSponsorship = (globalId?: string): UseChildSponsorshipReturn => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bioErrors, setBioErrors] = useState<ErrorCart[]>();
  const mountedRef = useRef(true);
  const router = useRouter();
  const searchParams = useSearchParams();
  const {
    actions: {
      addChild,
      goToCheckout
    }
  } = useCart('actions');
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);
  const handleSponsorshipSubmit = async () => {
    if (!globalId) {
      trackError('useChildSponsorship: missing globalId');
      return;
    }
    setIsSubmitting(true);
    setBioErrors(undefined);
    try {
      const errors = await addChild(globalId, searchParams.get('source') ?? undefined, searchParams.get('chid') ?? undefined);
      if (errors && errors.length > 0) {
        const hasAmHoldError = errors.some(e => e.type === AvailabilityErrorTypeEnum.ChildHold);
        if (hasAmHoldError) {
          router.replace('/sponsor-a-child/not-found');
          return;
        }
        const errorsToDisplay = errors.filter(err => !err.userShouldFix);
        if (errorsToDisplay.length > 0) {
          if (mountedRef.current) {
            setBioErrors(errorsToDisplay);
            setIsSubmitting(false);
          }
          return;
        }
      }

      // Proceed to checkout;
      goToCheckout();
    } catch (error) {
      console.error('Error adding children to cart:', error);
      trackError(`Error adding children to cart: ${error}`);
      if (mountedRef.current) {
        // Optionally surface a generic error to the UI; keep current shape so consumers decide
      }
    } finally {
      if (mountedRef.current) {
        setIsSubmitting(false);
      }
    }
  };
  return {
    isSubmitting,
    bioErrors,
    handleSponsorshipSubmit
  };
};